import { useCallback, useEffect, useState } from 'react';
import { useNodesInitialized, useReactFlow } from 'reactflow';
import { NodeId } from 'web';
import { zip } from 'lodash';
import { Node } from '@/types/node';
import {
  getAbsolutePositionFromRelative,
  getAdjacentPositionForNodeDuplication,
  getNodesBounds,
} from '@/components/Nodes/utils/nodeUtils';
import { useFlowView, useGroupsManagerView } from '@/components/Recipe/FlowContext';
import { DUPLICATE_NODE_OFFSET_X } from '@/components/Recipe/consts/ui';
import { useWorkflowStore } from '@/state/workflow.state';
import { usePendingAssetInsertions } from '@/state/pendingAssetInsertions.state';
import { UndoRedoEntry } from '@/components/Recipe/undoRedo';
import { useReactFlowViewportPositions } from './useReactFlowViewportPositions';

const POSITION_COLLISION_EPSILON = 1;
const MAX_POSITION_HOPS = 50;

export function useNodeDuplication() {
  const { getCenterPosition, isNodeInViewport } = useReactFlowViewportPositions();
  const { setViewport, getViewport } = useReactFlow();
  const getReactFlowContainerRect = useWorkflowStore((s) => s.getReactFlowContainerRect);
  const groupManagerView = useGroupsManagerView();
  const flowView = useFlowView();

  const calculatePastePositions = useCallback(
    (nodes: Node[], occupiedNodes: Node[]): Map<NodeId, Node['position']> => {
      if (!nodes.length) return new Map();
      const first = findTopLeftNode(nodes);
      const basePosition = nodes.some((node) => !isNodeInViewport(node))
        ? getCenterPosition()
        : getAdjacentPositionForNodeDuplication(first);
      const position = { ...basePosition };
      for (let i = 0; i < MAX_POSITION_HOPS && isOccupied(position, occupiedNodes); i++) {
        position.x += (first.width ?? 0) + DUPLICATE_NODE_OFFSET_X;
        position.y += (first.height ?? 0) / 3;
      }
      const positions = new Map([[first.id, position]]);
      const delta = { x: position.x - first.position.x, y: position.y - first.position.y };
      for (const node of nodes) {
        if (node.id !== first.id) {
          positions.set(node.id, { x: node.position.x + delta.x, y: node.position.y + delta.y });
        }
      }
      return positions;
    },
    [getCenterPosition, isNodeInViewport],
  );

  const prepareNodesForPaste = useCallback(
    (nodes: Node[]) => {
      const ids = new Set(nodes.map((node) => node.id));
      const normalized = nodes.map((node) => ({
        ...node,
        parentId: node.parentId && ids.has(node.parentId) ? node.parentId : undefined,
      }));
      const topLevel = normalized.filter((node) => !node.parentId);
      const pending = Object.values(usePendingAssetInsertions.getState().insertions)
        .filter((insertion) => insertion.workflowId === useWorkflowStore.getState().recipe.id)
        .flatMap((insertion) => insertion.nodes);
      const occupied = [...flowView.getNodes(), ...pending].filter((node) => !node.parentId);
      const positions = calculatePastePositions(topLevel, occupied);
      return normalized.map((node) => {
        const position = positions.get(node.id);
        return position ? { ...node, position } : node;
      });
    },
    [flowView, calculatePastePositions],
  );

  const positionPreparedNodes = useCallback(
    (nodes: Node[], ids: NodeId[]): UndoRedoEntry =>
      flowView.setNodePositions(
        nodes.flatMap((node, index) => (node.parentId ? [] : [{ nodeId: ids[index], position: node.position }])),
      ),
    [flowView],
  );

  const positionDuplicatedNodes = useCallback(
    (originalNodes: Node[], duplicatedNodesIds: NodeId[], placement: 'adjacent' | 'center' = 'adjacent'): UndoRedoEntry => {
      const undoEntry = UndoRedoEntry.empty();

      if (originalNodes.length === 0) {
        return undoEntry;
      }

      const newNodeIdByOriginal = Object.fromEntries(
        zip(
          originalNodes.map((node) => node.id),
          duplicatedNodesIds,
        ) as [NodeId, NodeId][],
      );

      // Position nodes that are in groups if parent isn't duplicated as well
      undoEntry.add(groupManagerView.positionDuplicatedChildrenInExistingGroups(originalNodes, duplicatedNodesIds));

      const originalNodesNotInGroups = originalNodes.filter((node) => !flowView.isNodeInGroup(node));

      if (originalNodesNotInGroups.length === 0) {
        return undoEntry;
      }

      const origTopLeftNode = findTopLeftNode(originalNodesNotInGroups);

      // Position the top-left node: if any node is outside viewport, center the viewport on the top-left node, otherwise position the top-left node adjacent to the original child node
      const isAnyNodeOutsideViewport = originalNodesNotInGroups.some((node) => !isNodeInViewport(node));
      const bounds = getNodesBounds(originalNodesNotInGroups);
      const center = getCenterPosition();
      const basePosition = placement === 'center'
        ? {
            x: center.x - bounds.width / 2 + origTopLeftNode.position.x - bounds.x,
            y: center.y - bounds.height / 2 + origTopLeftNode.position.y - bounds.y,
          }
        : isAnyNodeOutsideViewport
          ? center
          : getAdjacentPositionForNodeDuplication(origTopLeftNode);

      const step = {
        x: (origTopLeftNode.width ?? 0) + DUPLICATE_NODE_OFFSET_X,
        y: (origTopLeftNode.height ?? 0) / 3,
      };

      const placedIds = new Set(duplicatedNodesIds);
      const existingTopLevelNodes = flowView
        .getNodes()
        .filter((node) => !placedIds.has(node.id) && !flowView.isNodeInGroup(node));

      const newTopLeftPosition = { ...basePosition };
      
      let numHops = 0;
      while(placement !== 'center' && numHops < MAX_POSITION_HOPS && isOccupied(newTopLeftPosition, existingTopLevelNodes)) {
        newTopLeftPosition.x += step.x;
        newTopLeftPosition.y += step.y;
        numHops++;
      }

      undoEntry.add(
        flowView.setNodePositions([{ nodeId: newNodeIdByOriginal[origTopLeftNode.id], position: newTopLeftPosition }]),
      );

      const delta = {
        x: newTopLeftPosition.x - origTopLeftNode.position.x,
        y: newTopLeftPosition.y - origTopLeftNode.position.y,
      };

      // Position rest of the nodes relative to the top-left node
      for (const node of originalNodesNotInGroups) {
        if (node.id === origTopLeftNode.id) {
          continue;
        }

        undoEntry.add(
          flowView.setNodePositions([
            {
              nodeId: newNodeIdByOriginal[node.id],
              position: {
                x: node.position.x + delta.x,
                y: node.position.y + delta.y,
              },
            },
          ]),
        );
      }

      return undoEntry;
    },
    [getCenterPosition, isNodeInViewport, groupManagerView, flowView],
  );

  const positionDuplicatedNodesAtCenter = useCallback(
    (nodes: Node[], nodeIds: NodeId[]) => positionDuplicatedNodes(nodes, nodeIds, 'center'),
    [positionDuplicatedNodes],
  );

  const scrollNodeIntoViewIfNeeded = useCallback(
    (nodes: Node[]) => {
      if (nodes.length === 0) {
        return;
      }

      // Convert relative positions to absolute for nodes with parentId
      const nodesWithAbsolutePositions = nodes.map((node) => {
        if (node.parentId) {
          const parentNode = flowView.getNode(node.parentId);
          if (parentNode) {
            const absolutePosition = getAbsolutePositionFromRelative(node.position, parentNode);
            return {
              ...node,
              position: absolutePosition,
            };
          }
        }
        return node;
      });

      const areAllNodesInViewport = nodesWithAbsolutePositions.every((node) => isNodeInViewport(node));

      if (areAllNodesInViewport) {
        return;
      }

      // Get current viewport to preserve zoom
      const currentViewport = getViewport();

      // Calculate center position of all nodes in flow coordinates (using absolute positions)
      const nodesBounds = getNodesBounds(nodesWithAbsolutePositions);
      const centerX = nodesBounds.x + nodesBounds.width / 2;
      const centerY = nodesBounds.y + nodesBounds.height / 2;

      // Get container dimensions
      const containerRect = getReactFlowContainerRect();
      if (!containerRect) {
        return;
      }

      // Calculate screen center position
      const screenCenterX = (containerRect.right - containerRect.left) / 2;
      const screenCenterY = (containerRect.bottom - containerRect.top) / 2;

      // Calculate viewport position to center nodes on screen
      // Formula: screenX = flowX * zoom + viewportX
      // To center: screenCenterX = centerX * zoom + viewportX
      // Therefore: viewportX = screenCenterX - centerX * zoom
      const newViewportX = screenCenterX - centerX * currentViewport.zoom;
      const newViewportY = screenCenterY - centerY * currentViewport.zoom;

      // Pan to center without changing zoom - explicitly preserve zoom
      setViewport(
        {
          x: newViewportX,
          y: newViewportY,
          zoom: currentViewport.zoom,
        },
        { duration: 800 },
      );
    },
    [setViewport, getViewport, isNodeInViewport, getReactFlowContainerRect, flowView],
  );

  function findTopLeftNode(nodesWithAbsolutePositions: Node[]) {
    let topLeftNode = nodesWithAbsolutePositions[0];
    let minX = topLeftNode.position.x;
    let minY = topLeftNode.position.y;

    for (const node of nodesWithAbsolutePositions) {
      if (node.position.x < minX || (node.position.x === minX && node.position.y < minY)) {
        minX = node.position.x;
        minY = node.position.y;
        topLeftNode = node;
      }
    }
    return topLeftNode;
  }

  return {
    positionDuplicatedNodes,
    positionDuplicatedNodesAtCenter,
    prepareNodesForPaste,
    positionPreparedNodes,
    scrollNodeIntoViewIfNeeded,
  };
}

function isOccupied(position: { x: number; y: number }, existingTopLevelNodes: Node[]) {
  return existingTopLevelNodes.some(
    (node) =>
      Math.abs(node.position.x - position.x) < POSITION_COLLISION_EPSILON &&
      Math.abs(node.position.y - position.y) < POSITION_COLLISION_EPSILON,
  );
}

export function useFitViewWhenReady() {
  const nodesInitialized = useNodesInitialized();
  const { fitView } = useReactFlow();
  const [pendingFit, setPendingFit] = useState(false);

  const fitViewWhenReady = useCallback(() => {
    setPendingFit(true);
  }, []);

  useEffect(() => {
    if (!nodesInitialized || !pendingFit) return;
    setPendingFit(false);
    fitView();
  }, [nodesInitialized, pendingFit, fitView]);

  return fitViewWhenReady;
}
